import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { colors } from 'styles/colors';
import FAQAccordion from 'app/components/FAQAccordion';
import { PaymentResultContentProps } from './types';

const PaymentResultContent: React.FC<PaymentResultContentProps> = ({
  status,
  showNextSteps = false,
  nextStepsContent = [],
  showFAQ = false,
  faqData = [],
  customContent
}) => {
  // Default next steps for success status
  const defaultNextSteps = [
    {
      step: 1,
      title: 'Check your email for booking instructions and session details',
      description: '',
      color: colors.successGreen
    },
    {
      step: 2,
      title: 'Schedule your counselling session at your preferred time',
      description: '',
      color: colors.primaryBlue
    }
  ];

  const stepsToRender = nextStepsContent.length > 0 ? nextStepsContent : defaultNextSteps;

  return (
    <>
      {/* Next Steps Section */}
      {showNextSteps && status === 'success' && (
        <Card sx={{ mb: 4, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={600} color={colors.fontDarkBlue} sx={{ mb: 3 }}>
              What's Next?
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {stepsToRender.map((step) => (
                <Box key={step.step} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: step.color || colors.primaryBlue,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography color="white" sx={{ fontSize: '14px', fontWeight: 600 }}>
                      {step.step}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography color={colors.fontDarkBlue}>
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography variant="body2" color={colors.fontLight} sx={{ mt: 0.5 }}>
                        {step.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* FAQ Section */}
      {showFAQ && faqData.length > 0 && (
        <Card sx={{ boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={600} color={colors.fontDarkBlue} sx={{ mb: 3 }}>
              {status === 'failure' ? 'Need Help?' : 'Frequently Asked Questions'}
            </Typography>
            
            <FAQAccordion 
              faqData={faqData.map(item => ({
                question: item.question,
                answer: item.answer,
                defaultExpanded: item.defaultExpanded || false
              }))} 
              defaultExpandedIndex={0}
            />
          </CardContent>
        </Card>
      )}

      {/* Custom Content */}
      {customContent && (
        <Box sx={{ mt: 4 }}>
          {customContent}
        </Box>
      )}
    </>
  );
};

export default PaymentResultContent;